import { Link, useRouter, type ErrorComponentProps } from "@tanstack/react-router";
import { Button } from "@lato/client/components/ui/button";
import { logger } from "@lato/client/lib/logger";
import { LoadErrorState } from "@/components/LoadErrorState";

/**
 * The router's `defaultErrorComponent`: renders in place of the route that failed to load (a
 * rejected `beforeLoad`/`loader`, or a render error inside the route) while the surrounding
 * layout keeps working. Before this existed the router installed no catch boundary at all, so one
 * failed fetch during navigation replaced the entire app with the root boundary's crash panel
 * (2026-09-18 incident).
 *
 * "Try again" uses `router.invalidate()` rather than the boundary's `reset`: invalidate flips the
 * errored match back to pending and re-runs its `beforeLoad`, whereas `reset` alone re-renders a
 * match that is still in error and rethrows immediately.
 */
export function RouteLoadError(_props: ErrorComponentProps) {
  const router = useRouter();
  return (
    <LoadErrorState
      title="Couldn't load this page"
      description="Check your connection and try again."
      testId="route-load-error"
    >
      <Button variant="outline" onClick={() => void router.invalidate()}>
        Try again
      </Button>
      <Button asChild variant="ghost">
        <Link to="/studies">Go to studies</Link>
      </Button>
    </LoadErrorState>
  );
}

/** `defaultOnCatch`: keep the Logfire record that the root boundary used to emit for these. */
export function logRouteError(error: unknown, info?: { componentStack?: string | null }): void {
  const err = error instanceof Error ? error : new Error(String(error));
  logger.error("Route error caught by route boundary", err, {
    errorName: err.name,
    errorMessage: err.message,
    componentStack: info?.componentStack ?? undefined,
  });
}
